"use client"

import * as React from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"

import { cn } from "@/lib/utils"

function Tabs({
  className,
  value,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  value?: string
  onValueChange?: (value: string) => void
}) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      value={value}
      onValueChange={onValueChange}
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

// Canonical variants: "underline" | "pill" | "segmented".
// Legacy aliases (backward compat): "default" -> "underline", "segment" -> "segmented".
export type TabsListVariant =
  | "underline"
  | "pill"
  | "segmented"
  // legacy aliases — do not use in new code
  | "default"
  | "segment"

type ResolvedVariant = "underline" | "pill" | "segmented"

function resolveVariant(v: TabsListVariant | undefined): ResolvedVariant {
  if (v === "default" || v === undefined) return "underline"
  if (v === "segment") return "segmented"
  return v
}

const TabsListContext = React.createContext<{ variant: ResolvedVariant }>({
  variant: "underline",
})

function TabsList({
  className,
  variant = "underline",
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: TabsListVariant
}) {
  const resolved = resolveVariant(variant)

  const baseStyles =
    resolved === "segmented"
      ? "inline-flex w-fit items-center gap-1 bg-secondary/50 dark:bg-white/[0.03] p-1 rounded-[10px] border border-border/50 dark:border-white/[0.06]"
      : resolved === "pill"
        ? "inline-flex h-10 items-center gap-1"
        : "inline-flex h-10 items-center justify-start border-b border-border gap-6"

  return (
    <TabsListContext.Provider value={{ variant: resolved }}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={resolved}
        className={cn(baseStyles, className)}
        {...props}
      />
    </TabsListContext.Provider>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Tab>) {
  const context = React.useContext(TabsListContext)
  const variant = context?.variant || "underline"

  const sharedTransition =
    "transition-all duration-[0.12s] ease-[cubic-bezier(0.4,0,0.2,1)]"
  const sharedFocus =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
  const sharedDisabled =
    "disabled:pointer-events-none disabled:opacity-50"
  const sharedIcon =
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

  const styles =
    variant === "segmented"
      ? cn(
          "inline-flex items-center justify-center gap-1.5 px-3 py-1 text-sm font-medium whitespace-nowrap rounded-[6px] h-6",
          sharedTransition,
          sharedIcon,
          sharedFocus,
          sharedDisabled,
          "text-foreground/60 hover:text-foreground",
          "data-[active]:bg-background data-[active]:text-foreground data-[active]:shadow-sm data-[active]:font-semibold",
          "data-[active]:dark:bg-white/[0.05] data-[active]:dark:shadow-[0_0_0_1px_rgba(255,255,255,0.09),inset_0_1px_0_rgba(255,255,255,0.05)]"
        )
      : variant === "pill"
        ? cn(
            "inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium whitespace-nowrap rounded-[8px]",
            sharedTransition,
            sharedIcon,
            sharedFocus,
            sharedDisabled,
            "text-muted-foreground hover:bg-primary/10 hover:text-foreground",
            "data-[active]:bg-primary data-[active]:text-primary-foreground data-[active]:font-semibold"
          )
        : cn(
            "inline-flex items-center justify-center gap-1.5 px-1 py-2 text-sm font-medium whitespace-nowrap border-b-2 border-transparent",
            sharedTransition,
            sharedIcon,
            sharedFocus,
            sharedDisabled,
            "focus-visible:rounded-sm",
            "text-muted-foreground hover:text-foreground",
            "data-[active]:text-foreground data-[active]:border-primary data-[active]:font-semibold"
          )

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(styles, className)}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Panel>) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
